import { ArrowRight } from 'lucide-react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/lib/i18n';

/**
 * Internal cross-linking block — surfaces category / geo / consulting landings
 * from the parent page so Googlebot follows them instead of relying on the
 * sitemap alone. Keeps the linked pages out of "orphan page" state and passes
 * on internal PageRank. Body copy is deliberately utilitarian: no marketing
 * fluff, just clear signposts.
 */

export interface RelatedLink {
  href: string;   // absolute path without /en prefix (LocalizedLink handles it)
  labelDe: string;
  labelEn: string;
  descDe: string;
  descEn: string;
}

interface RelatedLandingsProps {
  headingDe?: string;
  headingEn?: string;
  introDe?: string;
  introEn?: string;
  links: RelatedLink[];
}

export function RelatedLandings({ headingDe, headingEn, introDe, introEn, links }: RelatedLandingsProps) {
  const t = useT();
  return (
    <section className="py-16 sm:py-24 bg-card border-t border-border">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-2xl mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold">
            {t(headingDe ?? 'Weitere Themen', headingEn ?? 'Related topics')}
          </h2>
          {(introDe || introEn) && (
            <p className="mt-3 text-muted-foreground">
              {t(introDe ?? '', introEn ?? '')}
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {links.map((link) => (
            <LocalizedLink
              key={link.href}
              to={link.href}
              className="group card-industrial p-5 hover:border-primary transition-colors"
              aria-label={t(link.labelDe, link.labelEn)}
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-base font-semibold group-hover:text-primary transition-colors">
                  {t(link.labelDe, link.labelEn)}
                </h3>
                <ArrowRight className="w-4 h-4 mt-1 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
              </div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {t(link.descDe, link.descEn)}
              </p>
            </LocalizedLink>
          ))}
        </div>
      </div>
    </section>
  );
}